import { HL7 } from '@/consts/systems.consts';
import { Encounter, Practitioner, Reference } from '@medplum/fhirtypes';

export function calculateEncounterDuration(encounter: Encounter): {
  hours: number;
  minutes: number;
  totalMinutes: number;
} {
  const startTime = encounter.period?.start
    ? new Date(encounter.period.start)
    : new Date();

  const now = new Date();
  const durationMs = now.getTime() - startTime.getTime();
  const hours = Math.floor(durationMs / (1000 * 60 * 60));
  const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
  const totalMinutes = Math.floor(durationMs / (1000 * 60));

  return { hours, minutes, totalMinutes };
}

export function formatEncounterDuration(
  hours: number,
  minutes: number
): string {
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

export function getEncounterReason(encounter: Encounter): string | undefined {
  const reason = encounter.reasonCode?.[0];
  if (!reason) {
    return undefined;
  }

  // Check for text reason first
  if (reason.text) {
    return reason.text;
  }

  // Check for coded reason
  if (reason.coding?.[0]?.display) {
    return reason.coding[0].display;
  }

  return undefined;
}

export function getReferringDoctor(
  encounter: Encounter
): Reference<Practitioner> | undefined {
  const refParticipant = encounter.participant?.find(
    (p) =>
      p.type?.[0]?.coding?.[0]?.code ===
      HL7.TERMINOLOGY.CS.V3.PARTICIPATION_TYPE.CODES.REFERRER
  );

  return refParticipant?.individual as Reference<Practitioner> | undefined;
}

export function getMainPractitioner(
  encounter: Encounter
): Reference<Practitioner> | undefined {
  const mainParticipant = encounter.participant?.find(
    (p) =>
      p.type?.[0]?.coding?.[0]?.code ===
      HL7.TERMINOLOGY.CS.V3.PARTICIPATION_TYPE.CODES.ATND
  );

  return mainParticipant?.individual as Reference<Practitioner> | undefined;
}

export function getConsultantPractitioners(
  encounter: Encounter
): Reference<Practitioner>[] {
  const consultants = encounter.participant?.filter(
    (p) =>
      p.type?.[0]?.coding?.[0]?.code ===
      HL7.TERMINOLOGY.CS.V3.PARTICIPATION_TYPE.CODES.CON
  );

  return consultants?.map((p) => p.individual as Reference<Practitioner>) || [];
}

export function getEncounterNotes(encounter: Encounter): string | undefined {
  const note = encounter.extension?.[0];
  if (!note) {
    return undefined;
  }

  // Check for text note first
  if (note.valueString) {
    return note.valueString;
  }

  return undefined;
}
